import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdBusinessOwnersOptions } from '@onefootprint/fetch-dashboard';
import type { Entity, UserTimeline, WorkflowSource } from '@onefootprint/request-types/dashboard';
import { EntityKind } from '@onefootprint/types';
import { useQuery } from '@tanstack/react-query';
export type AbandonedEvent = {
  kind: 'abandoned';
  data: {
    workflowSource: WorkflowSource;
  };
};
export type AwaitingBosEvent = {
  kind: 'awaiting-bos';
  data: Record<string, never>;
};

/** Extra timeline event types that aren't returned by the backend. These are only added by the frontend */
export type ExtraTimelineEvents = {
  event: AbandonedEvent | AwaitingBosEvent;
  timestamp: string;
  seqno: number;
  onboardingId: string | undefined;
  id: string | undefined;
};
export type AuditTrailTimelineEvent = UserTimeline | ExtraTimelineEvents;

/** Prefixes the timeline with events indicating when the entity's onboarding is incomplete, if needed. */
const useAddIncompleteEvents = (timeline, entity) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== entity.id) {
    t0 = getEntitiesByFpIdBusinessOwnersOptions({
      path: {
        fpId: entity.id
      }
    });
    $[0] = entity.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = entity.kind === EntityKind.business;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const bosQuery = useQuery(t2);
  const t3 = entity.workflows;
  let t4;
  if ($[5] !== bosQuery || $[6] !== entity.workflows || $[7] !== timeline) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedWorkflows = [...t3].sort(_temp);
      const latestOnboarding = sortedWorkflows[0];
      if (latestOnboarding && latestOnboarding?.status === "incomplete" && timeline.length > 0) {
        const hasPendingBos = bosQuery.data?.some(_temp2);
        const location = timeline.findIndex(event => event.onboardingId === latestOnboarding.id);
        if (location === -1) {
          t4 = timeline;
          break bb0;
        }
        const insertBeforeEvent = timeline[location];
        const event_0 = {
          event: hasPendingBos && latestOnboarding.playbook?.isExecutable ? {
            kind: "awaiting-bos",
            data: {}
          } : {
            kind: "abandoned",
            data: {
              workflowSource: latestOnboarding.source
            }
          },
          seqno: insertBeforeEvent.seqno,
          timestamp: insertBeforeEvent.timestamp,
          onboardingId: latestOnboarding.id,
          id: undefined
        };
        t4 = [...timeline.slice(0, location), event_0, ...timeline.slice(location)];
        break bb0;
      } else {
        t4 = timeline;
        break bb0;
      }
    }
    $[5] = bosQuery;
    $[6] = entity.workflows;
    $[7] = timeline;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
};
export default useAddIncompleteEvents;
function _temp(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
function _temp2(bo) {
  const isIncomplete = bo.boStatus === "incomplete" || bo.boStatus === "awaiting_kyc" || bo.boStatus === "pending";
  return bo.name && isIncomplete;
}